/**
 * Mobile app configuration
 */

// Determine API base URL based on environment
const getApiBaseUrl = (): string => {
  if (import.meta.env.DEV) {
    // In development, use proxy configured in vite.config.ts
    return '';
  }
  // In production, API is on same origin
  return '';
};

export const config = {
  // #525 — the UI follows the browser language on every load. A tag whose
  // primary subtag is in `supported` selects that language; anything else
  // gets `fallback`. A manual switch lasts the session only.
  // The server-injected disclaimer modal (src/server/disclaimer.py) applies
  // the same fr-or-en rule in its own script before the SPA boots; a new
  // language here needs the same addition there.
  locale: {
    supported: ['en', 'fr'],
    fallback: 'en'
  },

  // #527 — the visitor's approximate position from the network (CloudFront
  // viewer geo behind briefer.amenai.net/locate), so the tracker opens on a
  // map with no location prompt. City-level; `accuracyM` is what the fix
  // claims. In dev the Vite proxy forwards `/locate` to the briefer host.
  locate: {
    url: import.meta.env.DEV ? '/locate' : 'https://briefer.amenai.net/locate',
    timeoutMs: 4000,
    accuracyM: 20000,
    // A failed request is retried while the source stays armed — the loop
    // marks itself running once and would otherwise never ask again.
    retryMs: 8000,
    maxAttempts: 3
  },

  airports: {
    /**
     * What the client accepts as an ICAO code, mirroring the `^[A-Z0-9]{4}$`
     * every airport endpoint declares server-side (#512: digits, because the
     * airport table holds codes like KC83). Three places used to
     * carry their own rule and two of them only checked the LENGTH, so
     * `LF1G` was storable as a recent flight and came back from the server
     * as a 422 — which carries no did-you-mean and reads as "something
     * went wrong". Read through `isValidIcao`, never re-typed.
     */
    icaoPattern: '^[A-Z0-9]{4}$'
  },

  api: {
    baseUrl: getApiBaseUrl(),
    endpoints: {
      singleAirport: '/api/notam-geojson',
      airportsGeoJson: '/api/notam-geojson/airports',
      enrouteGeoJson: '/api/notam-geojson/enroute',
      nearby: '/api/notam/nearby',
      // #8 contextual proximity NOTAMs (API-key authenticated)
      contextualNotams: '/api/contextualNotams'
    },
    timeout: 30000
  },

  map: {
    tileLayers: {
      osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19
      },
      vfr: {
        url: '/tiles/vfr/{z}/{x}/{y}.png',
        attribution: 'VFR Charts &copy; <a href="https://www.openflightmaps.org">Open Flightmaps</a>',
        maxZoom: 18,
        minZoom: 7,
        minNativeZoom: 7,
        maxNativeZoom: 12,
        opacity: 0.8,
        tileSize: 256, // Server returns 512 but we request at 256
        errorTileUrl: '' // Hide missing tiles
      },
      openAip: {
        // OpenAIP tiles - requires valid API key from https://www.openaip.net/
        // Combined layer (airspaces, airports, navaids)
        url: 'https://api.tiles.openaip.net/api/data/openaip/{z}/{x}/{y}.png?apiKey={apiKey}',
        attribution: 'Airspace &copy; <a href="https://www.openaip.net">OpenAIP</a>',
        maxZoom: 14,
        minZoom: 7,
        opacity: 0.7,
        // Same key as desktop - may be rate-limited or expired
        // Get your own at https://www.openaip.net/ (free account)
        apiKey: '9c57f64de1291fa3c2aa7433eecfc99d'
      }
    },
    airportDiagramEndpoint: '/api/airport-diagram',
    // #539: the map asks for the airports inside its visible area on every
    // settled pan/zoom at or above diagramMinZoom, then fetches each diagram
    // through airportDiagramEndpoint. Below the zoom nothing is asked and
    // nothing already drawn is removed.
    airportDiagramAreaEndpoint: '/api/airport-diagram/inArea',
    diagramMinZoom: 11,
    diagramRefetchDebounceMs: 400,
    defaultCenter: [46.2276, 2.2137] as [number, number], // France center
    defaultZoom: 6,
    // #22: the zoom the map falls back to when a single-airport query
    // returns no NOTAMs and there is nothing to fit bounds against.
    defaultAirportZoom: 12,
    clustering: {
      maxClusterRadius: 50,
      disableClusteringAtZoom: 12,
      spiderfyOnMaxZoom: true
    }
  },

  offline: {
    notamCacheTTL: 30 * 60 * 1000, // 30 minutes
    tileCacheTTL: 7 * 24 * 60 * 60 * 1000, // 7 days
    maxRecentQueries: 10
  },

  features: {
    voiceSearch: true,
    pushNotifications: true,
    gpsNearby: true,
    // #8 Phase 3g /tracking visual surface. Shipping OFF by default per
    // PR #55 R5 review outcome: five xhigh review rounds surfaced
    // consistent finding counts (~15/round). The pattern matches the
    // saved [feedback_recursive_review_signal] memory — three design
    // pivots (#52, #54, R4 simplify) each closed one class of bugs and
    // opened another. Toggle on for internal QA / opt-in beta, then
    // iterate from real-user observation before flipping default. Known
    // limitations are enumerated in the PR #55 body under "Known
    // limitations shipped intentionally".
    contextualTracking: true
  },

  // #8 Phase 3g — geolocation tunables for /tracking. These are technical
  // defaults (not user preferences — those live in settingsStore as the dial
  // chips per D14). Lifted out of useContextualNotams.ts per PR #46 review F13
  // and CLAUDE.md "never edit code to change configuration".
  geolocation: {
    enableHighAccuracy: true,
    // Max age (ms) the browser may serve a cached position; balances battery
    // (higher = more cache hits) vs. freshness on a moving aircraft.
    maximumAge: 5000,
    // Per-fix timeout for watchPosition.
    timeout: 15000,
    // Forced /api/contextualNotams refresh cadence (ms) even if the aircraft
    // hasn't moved by `minDistanceM`. Per #8 design: 30s.
    forcedRefreshMs: 30_000,
    // Move threshold (meters) that fires an extra contextual query before
    // forcedRefreshMs elapses. Per #8 design: 500m.
    minDistanceM: 500,
    // #54 D2 threshold for the persistent-fail counter. Consecutive
    // POSITION_UNAVAILABLE / TIMEOUT / NaN-fix callbacks at or above this
    // value flip gpsState to 'persistent'; below it, 'transient'.
    persistentFailThreshold: 3,
    // #54 D3 — even without an explicit GPS error, a fix older than this
    // threshold triggers isStale (belt-and-suspenders against a silently-
    // hung receiver that reports neither errors nor updates). Set at 15s
    // so a stale in-flight position is caught within the same order of
    // magnitude as the persistent-fail escalation window.
    staleAgeMs: 15_000,
    // Longest the /tracking route guard will wait for the Permissions API
    // query before proceeding on what it already knows. The wait exists so
    // App.vue's immediate watcher does not arm watchPosition against the
    // default 'prompt' on a cold-start deep link; the bound exists so an
    // engine whose query never settles cannot stop the navigation.
    permissionSettleTimeoutMs: 2000
  },

  // #441 — the simulated flight leg. Speed, altitude, tick rate and time
  // acceleration are configuration, not literals in a component, so a
  // demo profile can be retuned without touching code.
  // #477: the tracking sheet's gesture and its three detents. Tuning the
  // drag for gloved hands, or moving a detent so it clears the ownship,
  // must not require editing a component.
  trackingSheet: {
    /** How far a pointer travels before it is a drag and not a tap. */
    dragThresholdPx: 24,
    /** Sheet height per detent, as a Tailwind height class. */
    detentHeightClass: {
      peek: 'h-auto',
      half: 'h-1/2',
      tall: 'h-[85%]'
    }
  },

  simulator: {
    // Tick rate (ms). The issue's code-footprint SLO is <= 1 Hz, and the
    // tick only recomputes a position — it does not call the API. Cadence
    // to the server stays whatever the real tracking loop decides from
    // `geolocation.minDistanceM` and `geolocation.forcedRefreshMs`.
    tickMs: 1000,
    // Default aircraft profile — a typical single-engine piston cruise.
    defaultGroundSpeedKt: 110,
    defaultAltitudeFt: 3000,
    // Reported fix accuracy (metres). A synthesized position is exact,
    // but reporting 0 would render a degenerate accuracy circle, so this
    // is a plausible GPS figure.
    accuracyM: 5,
    // Wall-clock multiplier. 1 = real time.
    defaultTimeAcceleration: 1,
    // Hard ceiling on the multiplier. The EFFECTIVE ceiling is lower
    // whenever the ground speed is high — see `apiBudgetFraction`.
    maxTimeAcceleration: 8,
    // The invariant that keeps a simulated leg from costing more than the
    // same real leg: one tick may advance at most this fraction of
    // `geolocation.minDistanceM`, the distance that triggers an extra
    // /api/contextualNotams call. Above it the loop would fire on EVERY
    // tick. The ceiling is therefore computed from the speed actually
    // being flown, not from the default — at 500 kt, 8x would advance
    // 2,058 m against a 500 m trigger, four times over budget.
    apiBudgetFraction: 0.95,
    // How much flight one tick may credit, in nominal ticks, however long
    // the gap really was. 1 keeps the budget above exact: a late or
    // throttled tick makes the simulated clock run slow rather than jump.
    maxCatchUpTicks: 1,
    // Control ranges and steps. In config so a demo profile can be
    // retuned without touching a component.
    minGroundSpeedKt: 40,
    maxGroundSpeedKt: 500,
    groundSpeedStepKt: 10,
    minAltitudeFt: 0,
    maxAltitudeFt: 45_000,
    altitudeStepFt: 500,
    // The multipliers the speed control cycles through. Anything above
    // maxTimeAcceleration is clamped by the provider, so this list and
    // the cap cannot disagree in the pilot's favour.
    accelerationSteps: [1, 2, 4, 8]
  }
};

export default config;
